import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { copyText, getJson, postJson } from "../api";
import { when } from "../ai/ai";
import { useAiData, useTitle } from "../ai/parts";
import {
  Counter,
  DataTable,
  FilterRow,
  Indicator,
  Indicators,
  IndicatorSkeleton,
  MoreFilters,
  NoResults,
  PeriodPill,
  Pill,
  PillDivider,
  RowsSkeleton,
  SearchBox,
  Section,
  useFilters,
  useIsMobile,
  Window,
  type FilterGroup,
} from "../ai/widgets";
import ui from "../ai/ai.module.css";
import { formatText, useTexts, type Label, type Locale } from "../texts";
import { ChoiceMenu } from "../ui/ChoiceMenu";
import { ChatIcon } from "../ui/icons/Icon";
import { Notice, PageHead, useToast } from "../ui/kit";
import { ListFooter, useServerList, type ServerList } from "../ui/list";
import { CodeBlock, CopyCode, PageState } from "../ui/pieces";
import { Sheet } from "../ui/Sheet";
import {
  durationChange,
  durationText,
  environmentName,
  hubModules,
  issueLine,
  issueSeverities,
  issueStates,
  issueTitle,
  moduleName,
  operationName,
  operationSearch,
  percentText,
  pointsChange,
  quickAction,
  releaseTrend,
  severityText,
  sharedQuery,
  stability,
  statusView,
  variationView,
  type Change,
  type HubIssue,
  type HubOccurrence,
  type HubOperation,
  type HubRelease,
} from "./hub";
import styles from "./hub.module.css";

// Error Hub (R7, especificación §5.10): Resumen · Problemas · Explorar · Rendimiento · Versiones en la barra del módulo.
// El período, el entorno, el módulo y la versión viven en la dirección y viajan entre las cinco secciones (E84). Sin permiso: C40
// «No tienes acceso a Error Hub»; si el Error Hub no responde: C40 con «Reintentar» (nunca 0 ni vacío, H-3). Listas
// paginadas en el Error Hub (D16); detalle de solo lectura en el panel C23 (320 px; hoja en el celular).

type HubSection = "summary" | "issues" | "explore" | "performance" | "releases";

const titleKey: Record<HubSection, string> = {
  summary: "ADM_HUB_SUMMARY",
  issues: "ADM_HUB_ISSUES",
  explore: "ADM_HUB_EXPLORE",
  performance: "ADM_HUB_PERFORMANCE",
  releases: "ADM_HUB_RELEASES",
};

/**
 * Sin el permiso del Error Hub (la sesión no lo tiene o el Error Hub respondió 403): el encabezado dice «Error Hub» (la barra
 * no dibuja sus secciones, AR4) y el bloque el motivo una sola vez (C40 1.14.8, AR3).
 */
export function HubForbidden({ section = "summary" }: { section?: HubSection }) {
  const { L } = useTexts();
  useTitle(L(titleKey[section]));
  return (
    <PageState
      head={<PageHead title={L("ADM_AREA_HUB")} />}
      title={L("ADM_HUB_FORBIDDEN")}
      line={L("ADM_FORBIDDEN_LINE")}
      action={
        <a className="tm-btn secondary" href="/">
          {L("ADM_GO_HOME")}
        </a>
      }
    />
  );
}

/** El Error Hub no respondió: la pantalla entera lo dice con «Reintentar». */
function Unavailable({ section, retry }: { section: HubSection; retry: () => void }) {
  const { L } = useTexts();
  return (
    <PageState
      head={<PageHead title={L(titleKey[section])} />}
      title={L("ADM_UNAVAILABLE")}
      line={L("ADM_HUB_UNAVAILABLE_LINE")}
      action={
        <button type="button" className="tm-btn secondary" onClick={retry}>
          {L("ADM_RETRY")}
        </button>
      }
    />
  );
}

/**
 * Opciones de «Más filtros»: las versiones y (R8) los entornos con datos en el período (se piden una vez por período).
 * Entornos null = el Error Hub no los dio: el grupo no se dibuja (nunca un grupo vacío).
 */
function useHubOptions(period: string) {
  const [options, setOptions] = useState<{ releases: string[]; environments: string[] | null }>({
    releases: [],
    environments: null,
  });
  useEffect(() => {
    let live = true;
    void getJson<{ releases: string[]; environments?: string[] | null }>(
      `/api/admin/errorhub/filters${period ? `?period=${encodeURIComponent(period)}` : ""}`,
    ).then(
      (reply) =>
        live &&
        reply.status === 200 &&
        reply.body &&
        setOptions({ releases: reply.body.releases, environments: reply.body.environments ?? null }),
    );
    return () => {
      live = false;
    };
  }, [period]);
  return options;
}

const stateKey: Record<string, string> = {
  attention: "ADM_HUB_STATE_ATTENTION",
  new: "ADM_HUB_STATE_NEW",
  investigating: "ADM_HUB_STATE_INVESTIGATING",
  regression: "ADM_HUB_STATE_REGRESSION",
  resolved: "ADM_HUB_STATE_RESOLVED",
  ignored: "ADM_HUB_STATE_IGNORED",
};

/**
 * Fila de filtros (C07): buscador (si la sección lo tiene), período con su selector de fechas, lo aplicado como
 * píldoras que se quitan y «Más filtros» con Entorno, Módulo y Versión (y en Problemas, Estado y Severidad). Todo en la
 * dirección; el entorno (R8) es compartido por las cinco secciones.
 */
function HubFilters({
  section,
  searchPlaceholder,
  defaultPeriod = "7d",
}: {
  section: HubSection;
  searchPlaceholder?: string;
  defaultPeriod?: string;
}) {
  const { L } = useTexts();
  const { get, set } = useFilters();
  const period = get("period") || defaultPeriod;
  const { releases, environments } = useHubOptions(get("period"));
  const env = get("env");
  const module = get("module");
  const release = get("release");
  const status = get("status");
  const severity = get("severity");
  const issueId = get("issueId");
  const groups: FilterGroup[] = [];
  if (section === "issues") {
    groups.push({
      key: "status",
      title: L("ADM_HUB_COL_STATUS"),
      options: Object.keys(stateKey).map((value) => ({ value, label: L(stateKey[value]!) })),
    });
    groups.push({
      key: "severity",
      title: L("ADM_HUB_COL_SEVERITY"),
      options: issueSeverities.map((value) => ({ value: value.toLowerCase(), label: severityText(L, value) })),
    });
  }
  // R8 · Entorno: los que tienen datos en el período; el elegido se conserva aunque ya no aparezca.
  if (environments && (environments.length > 0 || env))
    groups.push({
      key: "env",
      title: L("ADM_HUB_ENV"),
      options: (env && !environments.includes(env) ? [env, ...environments] : environments).map((value) => ({
        value,
        label: environmentName(L, value),
      })),
    });
  groups.push({
    key: "module",
    title: L("ADM_HUB_MODULE"),
    options: hubModules.map((value) => ({ value, label: moduleName(L, value) })),
  });
  if (section !== "releases")
    groups.push({
      key: "release",
      title: L("ADM_HUB_RELEASE"),
      options: (release && !releases.includes(release) ? [release, ...releases] : releases).map((value) => ({
        value,
        label: value,
      })),
    });
  const keys = groups.map((group) => group.key);
  const value = Object.fromEntries(keys.map((key) => [key, get(key)]));
  const applied = (key: string, text: string) => (
    <Pill key={key} pressed onClick={() => set({ [key]: "" })} label={`${L("ADM_AI_REMOVE_FILTER")}: ${text}`}>
      {text}
      <ChatIcon name="x" />
    </Pill>
  );
  return (
    <div className={ui.toolbar}>
      {searchPlaceholder ? (
        <SearchBox
          id={`hub-${section}-search`}
          value={get("search")}
          placeholder={searchPlaceholder}
          onChange={(text) => set({ search: text })}
        />
      ) : null}
      <FilterRow label={L("ADM_AI_FILTERS")}>
        <PeriodPill value={period} onChange={(next) => set({ period: next === defaultPeriod ? "" : next })} />
        {section === "issues" && status ? applied("status", L(stateKey[status] ?? "USAGE_UNKNOWN")) : null}
        {section === "issues" && severity ? applied("severity", severityText(L, severity.toUpperCase())) : null}
        {env ? applied("env", formatText(L("ADM_HUB_ENV_PILL"), environmentName(L, env))) : null}
        {module ? applied("module", moduleName(L, module)) : null}
        {section !== "releases" && release ? applied("release", formatText(L("ADM_HUB_RELEASE_PILL"), release)) : null}
        {section === "explore" && issueId ? applied("issueId", L("ADM_HUB_ONLY_ISSUE")) : null}
        {section === "issues" && get("organizationId") ? applied("organizationId", L("ADM_HUB_ONLY_ORG")) : null}
        <PillDivider />
        <MoreFilters
          groups={groups}
          value={value}
          onApply={(next) => set(Object.fromEntries(keys.map((key) => [key, next[key] ?? ""])))}
        />
      </FilterRow>
    </div>
  );
}

/** La consulta al BFF con los filtros de la dirección que usa la sección. */
function apiQuery(params: URLSearchParams, keys: string[]): string {
  const query = new URLSearchParams();
  for (const key of keys) {
    const value = params.get(key);
    if (value) query.set(key, value);
  }
  const text = query.toString();
  return text ? `?${text}` : "";
}

const hasFilters = (params: URLSearchParams, keys: string[]) => keys.some((key) => !!params.get(key));

// ---------------------------------------------------------------------------------------------------------------------
// Resumen

type Summary = {
  errors: number;
  affectedUsers: number;
  affectedOrganizations: number;
  newIssues: number;
  regressions: number;
  requests: number;
  errorRate: number | null;
  p95: number | null;
};

type SummaryReply = {
  current: Summary;
  previous: Summary;
  attention: { items: HubIssue[]; total: number };
  slow: { items: HubOperation[]; total: number };
};

/**
 * RC1 · K3 (C32 1.14.9, E54): la línea de un indicador comparado con el período anterior. Sin período anterior, el
 * contexto; si la diferencia no se ve en lo mostrado, «Sin cambios vs. período anterior»; si no, la diferencia.
 */
function changeLine(
  L: Label,
  change: Change,
  context: string,
  key: "ADM_HUB_VS_POINTS" | "ADM_HUB_VS_PERCENT",
): string {
  if (change.kind === "none") return context;
  if (change.kind === "same") return L("ADM_HUB_NO_CHANGE");
  return formatText(L(key), change.text);
}

/** `hub-resumen` · Error Hub › Resumen: indicadores enlazados, «Requieren atención» y «Más lentas». */
export function HubSummaryPage() {
  const { L, locale } = useTexts();
  const { params, set } = useFilters();
  useTitle(L("ADM_HUB_SUMMARY"));
  const data = useAiData<SummaryReply>(
    `/api/admin/errorhub/summary${apiQuery(params, ["period", "env", "module", "release"])}`,
  );
  const link = (path: string, extra: Record<string, string | null> = {}) => `${path}${sharedQuery(params, extra)}`;
  if (data.state === "failed" && data.error?.status === 403) return <HubForbidden />;
  if (data.state !== "loading" && !data.data) return <Unavailable section="summary" retry={data.retry} />;
  const head = (
    <>
      <PageHead title={L("ADM_HUB_SUMMARY")} sub={L("ADM_HUB_SUMMARY_SUB")} />
      <HubFilters section="summary" />
    </>
  );
  if (!data.data)
    return (
      <>
        {head}
        <IndicatorSkeleton count={5} label={L("ADM_HUB_INDICATORS")} />
        <RowsSkeleton rows={3} table />
      </>
    );
  const { current, previous, attention, slow } = data.data;
  const nowStability = stability(current.errorRate);
  const beforeStability = stability(previous.errorRate);
  // Hay período anterior si el Error Hub midió solicitudes en él (con 0, nunca «0 %» ni «0 puntos»: el contexto).
  const hasPrevious = previous.requests > 0;
  const requests = current.requests.toLocaleString(locale);
  const periodText = (params.get("period") || "7d") === "30d";
  return (
    <>
      {head}
      <Indicators label={L("ADM_HUB_INDICATORS")} count={5}>
        <Indicator
          label={L("ADM_HUB_STABILITY")}
          value={percentText(locale, nowStability)}
          muted={nowStability == null}
          line={
            nowStability == null
              ? L("ADM_HUB_NO_REQUESTS")
              : changeLine(
                  L,
                  pointsChange(locale, nowStability, beforeStability, hasPrevious),
                  formatText(L("ADM_HUB_STABILITY_CONTEXT"), requests),
                  "ADM_HUB_VS_POINTS",
                )
          }
          href={link("/ErrorHub/Issues")}
        />
        <Indicator
          label={L("ADM_HUB_P95")}
          value={durationText(locale, current.p95)}
          muted={current.p95 == null}
          line={
            current.p95 == null
              ? L("ADM_HUB_NO_REQUESTS")
              : changeLine(
                  L,
                  durationChange(locale, current.p95, previous.p95, hasPrevious),
                  formatText(L("ADM_HUB_P95_CONTEXT"), requests),
                  "ADM_HUB_VS_PERCENT",
                )
          }
          href={link("/ErrorHub/Performance")}
        />
        <Indicator
          label={L("ADM_HUB_ERRORS")}
          value={current.errors.toLocaleString(locale)}
          line={
            !hasPrevious && previous.errors === 0
              ? L("ADM_HUB_ERRORS_CONTEXT")
              : formatText(L("ADM_HUB_VS_COUNT"), previous.errors.toLocaleString(locale))
          }
          href={link("/ErrorHub/Explore")}
        />
        <Indicator
          label={L("ADM_HUB_USERS")}
          value={current.affectedUsers.toLocaleString(locale)}
          line={formatText(
            L(current.affectedOrganizations === 1 ? "ADM_HUB_IN_ORGS_ONE" : "ADM_HUB_IN_ORGS"),
            current.affectedOrganizations.toLocaleString(locale),
          )}
          href={link("/ErrorHub/Explore")}
        />
        <Indicator
          label={L("ADM_HUB_NEW")}
          value={current.newIssues.toLocaleString(locale)}
          line={L("ADM_HUB_NEW_LINE")}
          href={link("/ErrorHub/Issues", { status: "new" })}
        />
        <Indicator
          label={L("ADM_HUB_REGRESSIONS")}
          value={current.regressions.toLocaleString(locale)}
          line={L("ADM_HUB_REGRESSIONS_LINE")}
          href={link("/ErrorHub/Releases")}
        />
      </Indicators>
      <Section
        id="hub-attention"
        title={L("ADM_HUB_ATTENTION")}
        line={
          attention.total > attention.items.length
            ? formatText(L("ADM_HUB_SHOWING"), attention.items.length, attention.total.toLocaleString(locale))
            : undefined
        }
        action={
          <a className="tm-btn secondary" href={link("/ErrorHub/Issues", { status: "attention" })}>
            {L("ADM_HUB_SEE_ISSUES")}
          </a>
        }
      >
        {attention.items.length === 0 ? (
          <div className="tm-empty" role="status" data-part="vacio">
            <strong>{L("ADM_HUB_NO_ATTENTION")}</strong>
            <span>{L("ADM_HUB_NO_ATTENTION_LINE")}</span>
            <a className="tm-btn secondary" href={link("/ErrorHub/Issues")}>
              {L("ADM_HUB_SEE_ALL_ISSUES")}
            </a>
          </div>
        ) : (
          <IssueTable
            rows={attention.items}
            compact
            hrefOf={(row) => link("/ErrorHub/Issues", { status: "attention", issue: row.id })}
          />
        )}
      </Section>
      <Section
        id="hub-slow"
        title={L("ADM_HUB_SLOWEST")}
        line={
          slow.total > slow.items.length
            ? formatText(L("ADM_HUB_SHOWING"), slow.items.length, slow.total.toLocaleString(locale))
            : undefined
        }
        action={
          <a className="tm-btn secondary" href={link("/ErrorHub/Performance")}>
            {L("ADM_HUB_SEE_PERFORMANCE")}
          </a>
        }
      >
        {slow.items.length === 0 ? (
          <div className="tm-empty" role="status" data-part="vacio">
            <strong>{L("ADM_HUB_NO_OPERATIONS")}</strong>
            <span>{L("ADM_HUB_NO_OPERATIONS_LINE")}</span>
            {periodText ? null : (
              <button type="button" className="tm-btn secondary" onClick={() => set({ period: "30d" })}>
                {L("ADM_HUB_SEE_30D")}
              </button>
            )}
          </div>
        ) : (
          <OperationTable rows={slow.items} compact link={link} />
        )}
      </Section>
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Tablas compartidas

function StatusTag({ status }: { status: string | null }) {
  const { L } = useTexts();
  const view = statusView(L, status);
  return view.tone === "plain" ? (
    <span>{view.text}</span>
  ) : (
    <span className={`tm-tag ${view.tone === "ochre" ? "ochre" : ""} ${styles.tag}`} data-tone={view.tone}>
      {view.text}
    </span>
  );
}

/** C28 · problemas: Problema (título y su línea) · Estado · [Severidad] · Ocurrencias · Usuarios · [Última vez]. */
function IssueTable({
  rows,
  compact,
  hrefOf,
  selected,
}: {
  rows: HubIssue[];
  compact?: boolean;
  hrefOf: (row: HubIssue) => string;
  selected?: string | null;
}) {
  const { L, locale } = useTexts();
  return (
    <div className={styles.tableHost} data-selected={selected ?? undefined}>
      <DataTable<HubIssue>
        label={L("ADM_HUB_ISSUES")}
        rows={rows}
        rowKey={(row) => row.id}
        href={hrefOf}
        columns={[
          {
            key: "issue",
            title: L("ADM_HUB_COL_ISSUE"),
            render: (row) => (
              <span className={ui.cellMain} data-current={row.id === selected ? "" : undefined}>
                <strong>{issueTitle(L, row)}</strong>
                <span className={ui.cellSub}>{issueLine(L, row)}</span>
              </span>
            ),
          },
          { key: "status", title: L("ADM_HUB_COL_STATUS"), render: (row) => <StatusTag status={row.status} /> },
          ...(compact
            ? []
            : [
                {
                  key: "severity",
                  title: L("ADM_HUB_COL_SEVERITY"),
                  render: (row: HubIssue) => severityText(L, row.severity),
                },
              ]),
          {
            key: "occurrences",
            title: L(compact ? "ADM_HUB_COL_OCCURRENCES" : "ADM_HUB_COL_OCCURRENCES_SHORT"),
            numeric: true,
            render: (row) => row.occurrences.toLocaleString(locale),
          },
          {
            key: "users",
            title: L("ADM_HUB_COL_USERS"),
            numeric: true,
            render: (row) => row.users.toLocaleString(locale),
          },
          ...(compact
            ? [
                {
                  key: "last",
                  title: L("ADM_HUB_COL_LAST_SEEN"),
                  render: (row: HubIssue) => when(L, locale, row.lastSeen),
                },
              ]
            : []),
        ]}
        mobile={(row) => ({
          title: issueTitle(L, row),
          tag: <StatusTag status={row.status} />,
          line: [
            moduleName(L, row.module),
            formatText(
              L(row.occurrences === 1 ? "ADM_HUB_N_OCCURRENCES_ONE" : "ADM_HUB_N_OCCURRENCES"),
              row.occurrences.toLocaleString(locale),
            ),
            formatText(
              L(row.users === 1 ? "ADM_HUB_N_USERS_ONE" : "ADM_HUB_N_USERS"),
              row.users.toLocaleString(locale),
            ),
          ].join(" · "),
        })}
      />
    </div>
  );
}

/**
 * RC1 · K3 (AF4, D7, E26): la operación con su nombre del negocio. Una ruta que el catálogo no nombra se muestra tal
 * cual, monoespaciada, y va a pendientes con su ruta.
 */
function OperationName({ operation, inline }: { operation: string | null; inline?: boolean }) {
  const { L } = useTexts();
  const name = operationName(L, operation);
  const className = name.known ? styles.operation : `${styles.operation} ${styles.mono}`;
  return inline ? (
    <span className={className}>{name.text}</span>
  ) : (
    <strong className={className} data-route={name.known ? undefined : (operation ?? undefined)}>
      {name.text}
    </strong>
  );
}

/** Variación (C32 1.14.9): «—» sin período anterior, «Sin cambios» si redondea a 0, si no «+18 %». */
function variationLine(L: Label, locale: Locale, value: number | null | undefined): string {
  const view = variationView(locale, value);
  return view.kind === "none" ? "—" : view.kind === "same" ? L("ADM_HUB_TREND_SAME") : view.text;
}

function Variation({ value }: { value: number | null | undefined }) {
  const { L, locale } = useTexts();
  const view = variationView(locale, value);
  return <span className={view.kind === "change" ? undefined : styles.quiet}>{variationLine(L, locale, value)}</span>;
}

/** C28 · operaciones: Operación (enlace a Explorar filtrado) · Volumen · P95 · [Variación]. D7: P50/P99 no se muestran. */
function OperationTable({
  rows,
  compact,
  link,
}: {
  rows: HubOperation[];
  compact?: boolean;
  link: (path: string, extra?: Record<string, string | null>) => string;
}) {
  const { L, locale } = useTexts();
  const explore = (row: HubOperation) => link("/ErrorHub/Explore", { search: row.operation, module: row.module });
  return (
    <DataTable<HubOperation>
      label={L("ADM_HUB_OPERATIONS")}
      rows={rows}
      rowKey={(row) => `${row.operation}|${row.module}|${row.release}`}
      href={explore}
      columns={[
        {
          key: "operation",
          title: L("ADM_HUB_COL_OPERATION"),
          render: (row) => (
            <span className={ui.cellMain}>
              <OperationName operation={row.operation} />
              <span className={ui.cellSub}>
                {[moduleName(L, row.module), row.release ? formatText(L("ADM_HUB_RELEASE_PILL"), row.release) : null]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </span>
          ),
        },
        {
          key: "count",
          title: L("ADM_HUB_COL_VOLUME"),
          numeric: true,
          render: (row) => row.count.toLocaleString(locale),
        },
        { key: "p95", title: L("ADM_HUB_COL_P95"), numeric: true, render: (row) => durationText(locale, row.p95) },
        ...(compact
          ? []
          : [
              {
                key: "variation",
                title: L("ADM_HUB_COL_VARIATION"),
                numeric: true,
                render: (row: HubOperation) => <Variation value={row.variation} />,
              },
            ]),
      ]}
      mobile={(row) => ({
        title: <OperationName operation={row.operation} inline />,
        line: [
          formatText(L("ADM_HUB_VOLUME_LINE"), row.count.toLocaleString(locale)),
          `P95 ${durationText(locale, row.p95)}`,
          compact ? null : variationLine(L, locale, row.variation),
        ]
          .filter(Boolean)
          .join(" · "),
      })}
    />
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Panel de detalle (C23, 320 px; hoja en el celular)

function DetailPanel({
  title,
  label,
  onClose,
  children,
}: {
  title: string;
  label: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const { L } = useTexts();
  const mobile = useIsMobile();
  const panel = useRef<HTMLElement>(null);
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  });
  // Al abrirse, el foco entra al panel (su «Cerrar»); Esc lo cierra si no hay una ventana encima.
  useEffect(() => {
    panel.current?.querySelector<HTMLElement>("button")?.focus({ preventScroll: true });
  }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !document.querySelector("dialog[open]")) close.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  const body = (
    <aside ref={panel} className={styles.panel} aria-label={label} data-part="panel">
      <div className={styles.panelHead}>
        <h2>{title}</h2>
        <button
          type="button"
          className={styles.iconButton}
          aria-label={L("ADM_AI_CLOSE")}
          title={L("ADM_AI_CLOSE")}
          onClick={onClose}
        >
          <ChatIcon name="x" />
        </button>
      </div>
      <div className={styles.panelBody}>{children}</div>
    </aside>
  );
  if (!mobile) return body;
  // C27 1.14.9 (RC1 · K1): la hoja compartida, con el panel haciendo su propio desplazamiento.
  return (
    <Sheet label={label} onClose={onClose} full className={styles.sheet}>
      {body}
    </Sheet>
  );
}

function Facts({ items }: { items: { label: string; value: ReactNode }[] }) {
  return (
    <dl className={styles.facts} data-part="datos">
      {items.map((item) => (
        <div key={item.label}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

type HistoryEntry = {
  at: string;
  who: "me" | "system" | "support";
  name: string | null;
  from: string | null;
  to: string;
  severityFrom: string | null;
  severityTo: string | null;
};

type IssueDetail = { issue: HubIssue; history: HistoryEntry[]; context: unknown; canManage: boolean };

function whoText(L: Label, entry: Pick<HistoryEntry, "who" | "name">): string {
  if (entry.who === "system") return L("ADM_PPL_ACTOR_SYSTEM");
  if (entry.who === "me") return entry.name ?? L("ADM_HUB_YOU");
  return L("ADM_HUB_SUPPORT");
}

/**
 * Panel del problema: impacto, la explicación del estado, «Investigar» (Explorar filtrado por el problema), «Comparar
 * versiones», la acción del estado («Iniciar investigación», «Marcar como resuelto», «Reabrir») y «Gestionar…» (estado y
 * severidad) solo con el permiso de gestión, el historial y el contexto técnico (C53; D7: sirve para investigar).
 */
function IssuePanel({ issueId, onClose, onChanged }: { issueId: string; onClose: () => void; onChanged: () => void }) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const [params] = useSearchParams();
  const data = useAiData<IssueDetail>(`/api/admin/errorhub/issues/${issueId}`);
  const [managing, setManaging] = useState(false);
  const [busy, setBusy] = useState(false);
  const label = L("ADM_HUB_ISSUE");
  if (!data.data)
    return (
      <DetailPanel title={label} label={label} onClose={onClose}>
        {data.state === "loading" ? (
          <RowsSkeleton rows={4} />
        ) : data.state === "missing" ? (
          <p className={styles.note}>{L("ADM_HUB_ISSUE_GONE")}</p>
        ) : (
          <Notice
            tone="error"
            action={
              <button type="button" className="tm-btn secondary" onClick={data.retry}>
                {L("ADM_RETRY")}
              </button>
            }
          >
            {data.error?.status === 403 ? L("ADM_HUB_FORBIDDEN") : L("ADM_UNAVAILABLE_LINE")}
          </Notice>
        )}
      </DetailPanel>
    );
  const { issue, history, context, canManage } = data.data;
  const quick = canManage ? quickAction(issue.status) : null;
  const resolved =
    issue.status === "RESOLVED" ? history.find((entry) => entry.to === "RESOLVED" && entry.from !== "RESOLVED") : null;
  const change = async (status: string | null, severity: string | null, message: string) => {
    setBusy(true);
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(`/api/admin/errorhub/issues/${issueId}`, {
      status,
      severity,
    });
    setBusy(false);
    if (reply.status !== 200 || !reply.body?.saved) {
      toast.show(L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC"));
      return false;
    }
    void data.reload();
    onChanged();
    toast.show(L(message));
    return true;
  };
  return (
    <DetailPanel title={label} label={label} onClose={onClose}>
      <div className={styles.panelTitle}>
        <h3>{issueTitle(L, issue)}</h3>
        <div className={styles.tags}>
          <StatusTag status={issue.status} />
          <span className={`tm-tag ${styles.tag}`}>{severityText(L, issue.severity)}</span>
        </div>
      </div>
      {issue.status === "REGRESSION" ? (
        <p className={styles.note}>
          {issue.release ? formatText(L("ADM_HUB_REGRESSION_IN"), issue.release) : L("ADM_HUB_REGRESSION_LINE")}
        </p>
      ) : null}
      {resolved ? (
        <p className={styles.note}>
          {formatText(L("ADM_HUB_RESOLVED_BY"), when(L, locale, resolved.at, true), whoText(L, resolved))}
        </p>
      ) : null}
      <Facts
        items={[
          { label: L("ADM_HUB_COL_OCCURRENCES"), value: issue.occurrences.toLocaleString(locale) },
          { label: L("ADM_HUB_COL_USERS"), value: issue.users.toLocaleString(locale) },
          { label: L("ADM_HUB_ORGANIZATIONS"), value: issue.organizations.toLocaleString(locale) },
          { label: L("ADM_HUB_RELEASE"), value: issue.release ?? "—" },
          { label: L("ADM_HUB_FIRST_SEEN"), value: when(L, locale, issue.firstSeen) },
          { label: L("ADM_HUB_COL_LAST_SEEN"), value: when(L, locale, issue.lastSeen) },
        ]}
      />
      <div className={styles.actions} data-part="acciones">
        <a
          className="tm-btn primary"
          href={`/ErrorHub/Explore${sharedQuery(params, { issueId: issue.id, release: null })}`}
        >
          {L("ADM_HUB_INVESTIGATE")}
        </a>
        <a className="tm-btn secondary" href={`/ErrorHub/Releases${sharedQuery(params, { release: null })}`}>
          {L("ADM_HUB_COMPARE_RELEASES")}
        </a>
        {quick ? (
          <button
            type="button"
            className="tm-btn secondary"
            disabled={busy}
            aria-busy={busy || undefined}
            onClick={() => void change(quick.next, null, quick.toast)}
          >
            {L(quick.key)}
          </button>
        ) : null}
        {canManage ? (
          <button type="button" className="tm-btn secondary" onClick={() => setManaging(true)}>
            {L("ADM_HUB_MANAGE")}
          </button>
        ) : null}
      </div>
      <section className={styles.panelSection} aria-labelledby="hub-history">
        <h4 id="hub-history">{L("ADM_HUB_HISTORY")}</h4>
        <ol className={styles.history}>
          {history.map((entry, index) => (
            <li key={`${entry.at}-${index}`}>
              <strong>
                {entry.from
                  ? entry.from === entry.to
                    ? formatText(L("ADM_HUB_HISTORY_SEVERITY"), severityText(L, entry.severityTo))
                    : formatText(L("ADM_HUB_HISTORY_TO"), statusView(L, entry.to).text)
                  : L("ADM_HUB_HISTORY_CREATED")}
              </strong>
              <span>
                {when(L, locale, entry.at)} · {whoText(L, entry)}
              </span>
            </li>
          ))}
          <li>
            <strong>{L("ADM_HUB_HISTORY_APPEARED")}</strong>
            <span>
              {when(L, locale, issue.firstSeen)} · {L("ADM_PPL_ACTOR_SYSTEM")}
            </span>
          </li>
        </ol>
      </section>
      <section className={styles.panelSection}>
        <CodeBlock
          title={L("ADM_HUB_TECHNICAL")}
          value={
            context ?? {
              errorKey: issue.errorKey,
              module: issue.module,
              surface: issue.surface,
              category: issue.category,
              release: issue.release,
            }
          }
        />
      </section>
      {managing ? (
        <ManageWindow
          issue={issue}
          onClose={() => setManaging(false)}
          onSave={(status, severity) => change(status, severity, "ADM_HUB_UPDATED")}
        />
      ) : null}
    </DetailPanel>
  );
}

/** `gestionar` · «Gestionar problema»: Estado y Severidad (C08); Guardar → «Problema actualizado». */
function ManageWindow({
  issue,
  onClose,
  onSave,
}: {
  issue: HubIssue;
  onClose: () => void;
  onSave: (status: string | null, severity: string | null) => Promise<boolean>;
}) {
  const { L } = useTexts();
  const [status, setStatus] = useState(issue.status ?? "NEW");
  const [severity, setSeverity] = useState(issue.severity ?? "HIGH");
  const [busy, setBusy] = useState(false);
  const changed = status !== issue.status || severity !== issue.severity;
  const icons = {
    chevron: <ChatIcon name="chevron-down" />,
    check: <ChatIcon name="check" />,
  };
  return (
    <Window
      open
      title={L("ADM_HUB_MANAGE_TITLE")}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
            {L("ACTION_CANCEL")}
          </button>
          <button
            type="button"
            className="tm-btn primary"
            disabled={busy}
            aria-busy={busy || undefined}
            onClick={() => {
              if (!changed) {
                onClose();
                return;
              }
              setBusy(true);
              void onSave(status === issue.status ? null : status, severity === issue.severity ? null : severity).then(
                (done) => {
                  setBusy(false);
                  if (done) onClose();
                },
              );
            }}
          >
            {L("ADM_AI_SAVE")}
          </button>
        </>
      }
    >
      <div className={styles.windowFields}>
        <div className="tm-field">
          <label htmlFor="hub-manage-status">{L("ADM_HUB_COL_STATUS")}</label>
          <ChoiceMenu
            id="hub-manage-status"
            label={L("ADM_HUB_COL_STATUS")}
            value={status}
            options={issueStates.map((value) => ({ value, label: statusView(L, value).text }))}
            onChange={setStatus}
            {...icons}
          />
        </div>
        <div className="tm-field">
          <label htmlFor="hub-manage-severity">{L("ADM_HUB_COL_SEVERITY")}</label>
          <ChoiceMenu
            id="hub-manage-severity"
            label={L("ADM_HUB_COL_SEVERITY")}
            value={severity}
            options={issueSeverities.map((value) => ({ value, label: severityText(L, value) }))}
            onChange={setSeverity}
            {...icons}
          />
        </div>
      </div>
    </Window>
  );
}

/** Pantalla con lista y panel: la lista se angosta cuando el panel cabe al lado (≥ 1200 px). */
function WithPanel({ panel, children }: { panel: ReactNode; children: ReactNode }) {
  return (
    <div className={`${styles.layout}${panel ? "" : ` ${styles.closed}`}`}>
      <div className={styles.main}>{children}</div>
      {panel}
    </div>
  );
}

/** Lista paginada con su estado: esqueleto, «no disponible» con Reintentar, vacío con su salida, contador y paginado. */
function ListBody<T>({
  list,
  filtered,
  onClear,
  query,
  things,
  empty,
  counter,
  children,
}: {
  list: ServerList<T>;
  filtered: boolean;
  onClear: () => void;
  query: string;
  things: string;
  empty: ReactNode;
  counter: string;
  children: ReactNode;
}) {
  const { L } = useTexts();
  if (list.state === "loading") return <RowsSkeleton rows={5} table />;
  if (list.state !== "ready")
    return (
      <Notice
        tone="error"
        action={
          <button type="button" className="tm-btn secondary" onClick={list.reload}>
            {L("ADM_RETRY")}
          </button>
        }
      >
        {L("ADM_HUB_UNAVAILABLE_LINE")}
      </Notice>
    );
  if (list.items.length === 0)
    return filtered ? <NoResults query={query} things={things} onClear={onClear} /> : <>{empty}</>;
  return (
    <>
      <Counter>{counter}</Counter>
      {children}
      <ListFooter list={list} />
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Problemas

/** `hub-problemas` · Error Hub › Problemas: tabla paginada y el panel del problema abierto (`issue=` en la dirección). */
export function HubIssuesPage() {
  const { L, locale } = useTexts();
  const { params, set } = useFilters();
  const [, setParams] = useSearchParams();
  const navigate = useNavigate();
  useTitle(L("ADM_HUB_ISSUES"));
  const keys = ["period", "env", "module", "release", "search", "status", "severity", "organizationId"];
  const list = useServerList<HubIssue>(`/api/admin/errorhub/issues${apiQuery(params, keys)}`);
  const selected = params.get("issue");
  const hrefOf = (row: HubIssue) => {
    const next = new URLSearchParams(params);
    next.set("issue", row.id);
    return `/ErrorHub/Issues?${next}`;
  };
  const close = () => {
    const next = new URLSearchParams(params);
    next.delete("issue");
    setParams(next);
  };
  if (list.state === "forbidden") return <HubForbidden section="issues" />;
  const filtered = hasFilters(params, ["env", "module", "release", "search", "status", "severity", "organizationId"]);
  return (
    <WithPanel
      panel={selected ? <IssuePanel key={selected} issueId={selected} onClose={close} onChanged={list.reload} /> : null}
    >
      <PageHead title={L("ADM_HUB_ISSUES")} />
      <HubFilters section="issues" searchPlaceholder={L("ADM_HUB_SEARCH_ISSUE")} />
      <ListBody
        list={list}
        filtered={filtered}
        query={params.get("search") ?? ""}
        things={L("ADM_HUB_THINGS_ISSUES")}
        onClear={() =>
          params.get("search")
            ? set({ search: "" })
            : set({ env: "", module: "", release: "", status: "", severity: "", organizationId: "" })
        }
        counter={formatText(
          L(list.total === 1 ? "ADM_HUB_N_ISSUES_ONE" : "ADM_HUB_N_ISSUES"),
          list.total.toLocaleString(locale),
        )}
        empty={
          <div className="tm-empty" role="status" data-part="vacio">
            <strong>{L("ADM_HUB_NO_ISSUES")}</strong>
            <span>{L("ADM_HUB_NO_ISSUES_LINE")}</span>
            <button
              type="button"
              className="tm-btn secondary"
              onClick={() => navigate(`/ErrorHub${sharedQuery(params)}`)}
            >
              {L("ADM_HUB_GO_SUMMARY")}
            </button>
          </div>
        }
      >
        <IssueTable rows={list.items} hrefOf={hrefOf} selected={selected} />
      </ListBody>
    </WithPanel>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Explorar

type OccurrenceDetail = {
  occurrence: HubOccurrence;
  context: unknown;
  trace: unknown[] | null;
};

/** Panel de la ocurrencia: referencia, usuario, empresa, fecha, traza, «Copiar contexto» y el contexto técnico (C53). */
function OccurrencePanel({ occurrenceId, onClose }: { occurrenceId: string; onClose: () => void }) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const data = useAiData<OccurrenceDetail>(`/api/admin/errorhub/occurrences/${occurrenceId}`);
  const label = L("ADM_HUB_OCCURRENCE");
  if (!data.data)
    return (
      <DetailPanel title={label} label={label} onClose={onClose}>
        {data.state === "loading" ? (
          <RowsSkeleton rows={4} />
        ) : data.state === "missing" ? (
          <p className={styles.note}>{L("ADM_HUB_OCCURRENCE_GONE")}</p>
        ) : (
          <Notice
            tone="error"
            action={
              <button type="button" className="tm-btn secondary" onClick={data.retry}>
                {L("ADM_RETRY")}
              </button>
            }
          >
            {L("ADM_UNAVAILABLE_LINE")}
          </Notice>
        )}
      </DetailPanel>
    );
  const { occurrence, context, trace } = data.data;
  const contextText = JSON.stringify(context, null, 2);
  return (
    <DetailPanel title={label} label={label} onClose={onClose}>
      <div className={styles.panelTitle}>
        <h3>{issueTitle(L, occurrence)}</h3>
        {/* C52 · la referencia se dicta o se pega en soporte: copiable (antes, «Copiar referencia» en cada fila). */}
        {occurrence.reference ? <CopyCode code={occurrence.reference} /> : null}
      </div>
      <Facts
        items={[
          {
            label: L("ADM_HUB_COL_USER"),
            value: occurrence.userName ?? occurrence.userEmail ?? L("ADM_PPL_ACTOR_SYSTEM"),
          },
          { label: L("ADM_COMPANY"), value: occurrence.organizationName ?? "—" },
          { label: L("ADM_HUB_COL_DATE"), value: when(L, locale, occurrence.occurredAt) },
          {
            label: L("ADM_HUB_TRACE"),
            value: occurrence.traceId ? <span className={styles.mono}>{occurrence.traceId.slice(0, 16)}</span> : "—",
          },
        ]}
      />
      <div className={styles.actions} data-part="acciones">
        <button
          type="button"
          className="tm-btn secondary"
          onClick={() =>
            void copyText(contextText).then((done) =>
              toast.show(L(done ? "ADM_HUB_CONTEXT_COPIED" : "ADM_COPY_FAILED")),
            )
          }
        >
          <ChatIcon name="copy" />
          {L("ADM_HUB_COPY_CONTEXT")}
        </button>
      </div>
      <section className={styles.panelSection}>
        <CodeBlock title={L("ADM_HUB_TECHNICAL")} value={context} />
      </section>
      {trace ? (
        <section className={styles.panelSection}>
          <CodeBlock title={L("ADM_HUB_TRACE_SPANS")} value={trace} />
        </section>
      ) : null}
    </DetailPanel>
  );
}

/** `hub-explorar` · Error Hub › Explorar: ocurrencias con su referencia y el panel de la abierta (`occurrence=`). */
export function HubExplorePage() {
  const { L, locale } = useTexts();
  const { params, set } = useFilters();
  const [, setParams] = useSearchParams();
  useTitle(L("ADM_HUB_EXPLORE"));
  const keys = ["period", "env", "module", "release", "search", "issueId"];
  const list = useServerList<HubOccurrence>(`/api/admin/errorhub/occurrences${apiQuery(params, keys)}`);
  const selected = params.get("occurrence");
  const hrefOf = (row: HubOccurrence) => {
    const next = new URLSearchParams(params);
    next.set("occurrence", row.id);
    return `/ErrorHub/Explore?${next}`;
  };
  const close = () => {
    const next = new URLSearchParams(params);
    next.delete("occurrence");
    setParams(next);
  };
  if (list.state === "forbidden") return <HubForbidden section="explore" />;
  const user = (row: HubOccurrence) => row.userName ?? row.userEmail ?? L("ADM_PPL_ACTOR_SYSTEM");
  return (
    <WithPanel panel={selected ? <OccurrencePanel key={selected} occurrenceId={selected} onClose={close} /> : null}>
      <PageHead title={L("ADM_HUB_EXPLORE")} />
      <HubFilters section="explore" searchPlaceholder={L("ADM_HUB_SEARCH_OCCURRENCE")} />
      <ListBody
        list={list}
        filtered={hasFilters(params, ["env", "module", "release", "search", "issueId"])}
        query={params.get("search") ?? ""}
        things={L("ADM_HUB_THINGS_OCCURRENCES")}
        onClear={() =>
          params.get("search") ? set({ search: "" }) : set({ env: "", module: "", release: "", issueId: "" })
        }
        counter={formatText(
          L(list.total === 1 ? "ADM_HUB_N_OCCURRENCES_ONE" : "ADM_HUB_N_OCCURRENCES"),
          list.total.toLocaleString(locale),
        )}
        empty={
          <div className="tm-empty" role="status" data-part="vacio">
            <strong>{L("ADM_HUB_NO_OCCURRENCES")}</strong>
            <span>{L("ADM_HUB_NO_OCCURRENCES_LINE")}</span>
            {(params.get("period") || "7d") === "30d" ? null : (
              <button type="button" className="tm-btn secondary" onClick={() => set({ period: "30d" })}>
                {L("ADM_HUB_SEE_30D")}
              </button>
            )}
          </div>
        }
      >
        <div className={styles.tableHost}>
          <DataTable<HubOccurrence>
            label={L("ADM_HUB_THINGS_OCCURRENCES")}
            rows={list.items}
            rowKey={(row) => row.id}
            href={hrefOf}
            columns={[
              {
                key: "reference",
                title: L("ADM_HUB_COL_REFERENCE"),
                render: (row) => (
                  <span className={ui.cellMain} data-current={row.id === selected ? "" : undefined}>
                    <strong>{issueTitle(L, row)}</strong>
                    <span className={`${ui.cellSub} ${styles.mono}`}>{row.reference ?? "—"}</span>
                  </span>
                ),
              },
              {
                key: "user",
                title: L("ADM_HUB_COL_USER"),
                render: (row) => (
                  <span className={ui.cellMain}>
                    <span>{user(row)}</span>
                    <span className={ui.cellSub}>{row.userName ? (row.userEmail ?? "—") : "—"}</span>
                  </span>
                ),
              },
              { key: "org", title: L("ADM_COMPANY"), render: (row) => row.organizationName ?? "—" },
              { key: "date", title: L("ADM_HUB_COL_DATE"), render: (row) => when(L, locale, row.occurredAt) },
            ]}
            mobile={(row) => ({
              title: issueTitle(L, row),
              line: [row.reference, user(row), row.organizationName, when(L, locale, row.occurredAt)]
                .filter(Boolean)
                .join(" · "),
            })}
          />
        </div>
      </ListBody>
    </WithPanel>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Rendimiento

/** `hub-rendimiento` · Error Hub › Rendimiento: operaciones con volumen, P95 y variación; cada una lleva a Explorar. */
export function HubPerformancePage() {
  const { L, locale } = useTexts();
  const { params, set } = useFilters();
  useTitle(L("ADM_HUB_PERFORMANCE"));
  // «Buscar operación» busca por el nombre que se ve (RC1 · K3): el Error Hub recibe la ruta de esa operación.
  const search = params.get("search");
  const apiParams = new URLSearchParams(params);
  if (search) apiParams.set("search", operationSearch(L, search));
  const list = useServerList<HubOperation>(
    `/api/admin/errorhub/performance${apiQuery(apiParams, ["period", "env", "module", "release", "search"])}`,
  );
  if (list.state === "forbidden") return <HubForbidden section="performance" />;
  const link = (path: string, extra: Record<string, string | null> = {}) => `${path}${sharedQuery(params, extra)}`;
  return (
    <>
      <PageHead title={L("ADM_HUB_PERFORMANCE")} />
      <HubFilters section="performance" searchPlaceholder={L("ADM_HUB_SEARCH_OPERATION")} />
      <p className={styles.help}>{L("ADM_HUB_HOW_TO_READ")}</p>
      <ListBody
        list={list}
        filtered={hasFilters(params, ["env", "module", "release", "search"])}
        query={params.get("search") ?? ""}
        things={L("ADM_HUB_THINGS_OPERATIONS")}
        onClear={() => (params.get("search") ? set({ search: "" }) : set({ env: "", module: "", release: "" }))}
        counter={formatText(
          L(list.total === 1 ? "ADM_HUB_N_OPERATIONS_ONE" : "ADM_HUB_N_OPERATIONS"),
          list.total.toLocaleString(locale),
        )}
        empty={
          <div className="tm-empty" role="status" data-part="vacio">
            <strong>{L("ADM_HUB_NO_OPERATIONS")}</strong>
            <span>{L("ADM_HUB_NO_OPERATIONS_LINE")}</span>
            {(params.get("period") || "7d") === "30d" ? null : (
              <button type="button" className="tm-btn secondary" onClick={() => set({ period: "30d" })}>
                {L("ADM_HUB_SEE_30D")}
              </button>
            )}
          </div>
        }
      >
        <OperationTable rows={list.items} link={link} />
      </ListBody>
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Versiones

/** `hub-versiones` · Error Hub › Versiones: la versión actual frente a la anterior y la tabla por versión con enlaces. */
export function HubReleasesPage() {
  const { L, locale } = useTexts();
  const { params, set } = useFilters();
  useTitle(L("ADM_HUB_RELEASES"));
  const list = useServerList<HubRelease>(
    `/api/admin/errorhub/releases${apiQuery(params, ["period", "env", "module"])}`,
  );
  if (list.state === "forbidden") return <HubForbidden section="releases" />;
  const period = params.get("period") || "30d";
  // Desde Versiones se va a Problemas y Explorar con el mismo período (30 días si no se eligió otro).
  const link = (path: string, extra: Record<string, string | null>) =>
    `${path}${sharedQuery(params, { period, ...extra })}`;
  const current = list.extra?.current as HubRelease | null | undefined;
  const previous = list.extra?.previous as HubRelease | null | undefined;
  return (
    <>
      <PageHead title={L("ADM_HUB_RELEASES")} />
      <HubFilters section="releases" defaultPeriod="30d" />
      {list.state === "ready" && current && previous ? (
        <Comparison
          current={current}
          previous={previous}
          href={link("/ErrorHub/Issues", { release: current.release })}
        />
      ) : null}
      <ListBody
        list={list}
        filtered={hasFilters(params, ["env", "module"])}
        query=""
        things={L("ADM_HUB_THINGS_RELEASES")}
        onClear={() => set({ env: "", module: "" })}
        counter={formatText(
          L(list.total === 1 ? "ADM_HUB_N_RELEASES_ONE" : "ADM_HUB_N_RELEASES"),
          list.total.toLocaleString(locale),
        )}
        empty={
          <div className="tm-empty" role="status" data-part="vacio">
            <strong>{L("ADM_HUB_NO_RELEASES")}</strong>
            <span>{L("ADM_HUB_NO_RELEASES_LINE")}</span>
            {period === "90d" ? null : (
              <button type="button" className="tm-btn secondary" onClick={() => set({ period: "90d" })}>
                {L("ADM_HUB_SEE_90D")}
              </button>
            )}
          </div>
        }
      >
        <DataTable<HubRelease>
          label={L("ADM_HUB_RELEASES")}
          rows={list.items}
          rowKey={(row) => row.release ?? ""}
          columns={[
            { key: "release", title: L("ADM_HUB_COL_RELEASE"), render: (row) => <strong>{row.release ?? "—"}</strong> },
            {
              key: "rate",
              title: L("ADM_HUB_COL_ERRORS"),
              numeric: true,
              render: (row) => percentText(locale, row.errorRate),
            },
            { key: "p95", title: L("ADM_HUB_COL_P95"), numeric: true, render: (row) => durationText(locale, row.p95) },
            {
              key: "new",
              title: L("ADM_HUB_COL_NEW"),
              numeric: true,
              render: (row) => (
                <a className={styles.cellLink} href={link("/ErrorHub/Issues", { release: row.release, status: "new" })}>
                  {row.newIssues.toLocaleString(locale)}
                </a>
              ),
            },
            {
              key: "regressions",
              title: L("ADM_HUB_COL_REGRESSIONS"),
              numeric: true,
              render: (row) => (
                <a
                  className={styles.cellLink}
                  href={link("/ErrorHub/Issues", { release: row.release, status: "regression" })}
                >
                  {row.regressions.toLocaleString(locale)}
                </a>
              ),
            },
            {
              key: "occurrences",
              title: L("ADM_HUB_COL_OCCURRENCES"),
              numeric: true,
              render: (row) => (
                <a className={styles.cellLink} href={link("/ErrorHub/Explore", { release: row.release })}>
                  {row.occurrences.toLocaleString(locale)}
                </a>
              ),
            },
          ]}
          mobile={(row) => ({
            title: row.release ?? "—",
            line: [
              formatText(L("ADM_HUB_ERRORS_LINE"), percentText(locale, row.errorRate)),
              `P95 ${durationText(locale, row.p95)}`,
              formatText(
                L(row.newIssues === 1 ? "ADM_HUB_N_NEW_ONE" : "ADM_HUB_N_NEW"),
                row.newIssues.toLocaleString(locale),
              ),
              formatText(
                L(row.regressions === 1 ? "ADM_HUB_N_REGRESSIONS_ONE" : "ADM_HUB_N_REGRESSIONS"),
                row.regressions.toLocaleString(locale),
              ),
            ].join(" · "),
          })}
          href={(row) => link("/ErrorHub/Issues", { release: row.release })}
        />
      </ListBody>
    </>
  );
}

/** C32 · la versión actual frente a la anterior: tendencia, errores y P95 con su valor de antes; lleva a Problemas. */
function Comparison({ current, previous, href }: { current: HubRelease; previous: HubRelease; href: string }) {
  const { L, locale } = useTexts();
  const trend = releaseTrend(current, previous, locale);
  const key = {
    same: "ADM_HUB_TREND_SAME",
    worse: "ADM_HUB_TREND_WORSE",
    better: "ADM_HUB_TREND_BETTER",
    mixed: "ADM_HUB_TREND_MIXED",
  }[trend];
  return (
    <a className={`${ui.kpi} ${ui.kpiLink} ${styles.comparison}`} href={href} data-part="indicador" data-trend={trend}>
      <span className={ui.kpiLabel}>
        {formatText(L("ADM_HUB_VERSUS"), current.release ?? "—", previous.release ?? "—")}
      </span>
      <span className={styles.comparisonTitle}>{L(key)}</span>
      <span className={styles.comparisonLine}>
        {formatText(
          L("ADM_HUB_COMPARE_LINE"),
          percentText(locale, current.errorRate),
          percentText(locale, previous.errorRate),
          durationText(locale, current.p95),
          durationText(locale, previous.p95),
        )}
      </span>
    </a>
  );
}
